import { makeLatestOnly, type LatestOnly } from "./latestOnly";

/** Dueño de una lista de partidos: la sesión/torneo/división bajo los que se
 * cargó (estampado al RESOLVER el fetch, nunca desde la selección vigente). */
export interface MatchesOwner {
  token: string;
  tournamentId: number;
  divisionId: number | null;
}

/** Boleto de una mutación del cuadro (16º gate de #135): captura el scope
 * EXACTO bajo el que se abrió (sesión+torneo+división, con época — A→B→A no
 * revive a la primera A) y su attempt (una mutación más nueva caduca a la
 * vieja). */
export interface MutationTicket {
  scopeId: string;
  attempt: number;
}

export interface MatchesOwnerLifecycle {
  /** Arranca un load del productor DECLARANDO el scope vigente
   * (sesión+torneo+división). Cambiar de scope abre una época nueva:
   * boletos de mutación de scopes anteriores quedan caducos aunque el
   * nombre del scope se repita (A→B→A). El token identifica ESTE load. */
  beginLoad(scope: string): number;
  /** ¿Este load sigue vigente? Verificar tras CADA await, antes de setters. */
  isCurrent(token: number): boolean;
  /** Estampa al dueño SOLO si el load sigue vigente Y no hay ninguna
   * mutación del cuadro pendiente; regresa si aplicó. */
  stampOwner(token: number, owner: MatchesOwner): boolean;
  /** ABRE una mutación del cuadro (regenerar/swap/editar): caduca cualquier
   * load() en vuelo, cierra el snapshot (owner=null) EN EL MISMO ACTO y,
   * mientras la mutación siga abierta, stampOwner rehúsa — un reload de una
   * mutación anterior no puede reabrir ownership a media mutación nueva.
   * Devuelve el boleto que ata la mutación a su scope y attempt. */
  beginMutation(): MutationTicket;
  /** ¿La mutación sigue vigente? Falso si cambió el scope (división/sesión,
   * incluso A→B→A), si una mutación más nueva abrió, o tras dispose. Una
   * mutación stale no ejecuta setters ni onError. */
  isMutationCurrent(ticket: MutationTicket): boolean;
  /** ¿El scope del boleto sigue siendo el vigente (y sin dispose)? Gate del
   * reload/finish: una mutación superada por otra DEL MISMO scope puede ser
   * la última en cerrar y debe recargar; una de OTRO scope, jamás. */
  isMutationScopeCurrent(ticket: MutationTicket): boolean;
  /** ¿Queda alguna mutación abierta DEL SCOPE del boleto? (17º gate: el
   * conteo es por scopeId — una mutación vieja de A jamás bloquea a B). */
  hasOpenMutations(ticket: MutationTicket): boolean;
  /** Registra que una mutación del grupo pidió conservar el error (17º
   * gate): keepError se AGREGA por grupo de mutaciones del mismo scope
   * hasta el reload único final. */
  noteKeepError(ticket: MutationTicket): void;
  /** Consume el keepError agregado del grupo del boleto (y lo limpia). */
  consumeKeepError(ticket: MutationTicket): boolean;
  /** Cierra la mutación del boleto; el reload de SU camino va DESPUÉS de
   * esto (solo entonces un load fresco puede volver a estampar). Corre
   * SIEMPRE, también para mutaciones stale. */
  endMutation(ticket: MutationTicket): void;
  /** Caduca loads en vuelo Y cierra el snapshot, sin abrir mutación. */
  invalidate(): void;
  /** DISPOSE PERMANENTE (13º gate de #135) — unmount del productor: todo
   * queda muerto para siempre. beginLoad ya no puede "reabrir" el lifecycle
   * (sus tokens nacen caducos), isCurrent/stampOwner son false, y ninguna
   * vía vuelve a llamar setOwner. Irreversible. */
  dispose(): void;
  /** ¿El productor ya fue desmontado? Los handlers lo consultan antes de
   * relanzar reloads o tocar setters desde un finally tardío. */
  isDisposed(): boolean;
}

/** Lifecycle del productor de matches (10º-11º gates de #135): guard
 * latest-only + disciplina de estampado del dueño en una sola pieza. La
 * carrera cerrada: un load iniciado ANTES de una regeneración no puede
 * re-estampar owner/matches viejos al resolver — invalidate() lo caduca y
 * cierra el snapshot en el mismo acto; solo un load FRESCO re-estampa.
 * Puro (sin React) para regresionar la carrera exacta sin montar la página. */
export function makeMatchesOwnerLifecycle(
  setOwner: (owner: MatchesOwner | null) => void,
): MatchesOwnerLifecycle {
  const guard: LatestOnly = makeLatestOnly();
  // Mutaciones del cuadro ABIERTAS (12º gate de #135): mientras haya alguna,
  // ningún load — ni siquiera uno fresco — puede re-estampar al dueño. Un
  // catch/reload de la mutación A que arranca después del beginMutation de B
  // queda vigente como load, pero su estampado se rehúsa hasta que B cierre.
  let disposed = false;
  // Identidad de scope con ÉPOCA (16º gate): cada cambio de scope declarado
  // por beginLoad abre época nueva — un boleto de mutación de la época
  // anterior queda caduco aunque el nombre del scope se repita (A→B→A).
  let lastScopeName: string | null = null;
  let scopeEpoch = 0;
  let activeScopeId = "";
  let mutationAttempt = 0;
  // Conteo de mutaciones abiertas POR scopeId (17º gate): una mutación
  // vieja del scope A no puede bloquear el estampado del scope B.
  const pendingByScope = new Map<string, number>();
  // keepError AGREGADO por grupo de mutaciones del mismo scope (17º gate):
  // si cualquiera del grupo pidió conservar el error, el reload único final
  // lo conserva — la última en cerrar no lo pisa con su propio false.
  const keepErrorByScope = new Map<string, boolean>();
  return {
    beginLoad: (scope: string) => {
      if (scope !== lastScopeName) {
        lastScopeName = scope;
        scopeEpoch += 1;
        activeScopeId = `${scope}#${scopeEpoch}`;
        // Scope nuevo: los keepError de scopes muertos ya no aplican
        // (jamás habrá reload de ellos).
        for (const key of Array.from(keepErrorByScope.keys())) {
          if (key !== activeScopeId) keepErrorByScope.delete(key);
        }
      }
      return guard.begin();
    },
    isCurrent: (token: number) => !disposed && guard.isCurrent(token),
    stampOwner: (token: number, owner: MatchesOwner) => {
      if (disposed) return false;
      // Solo bloquean las mutaciones abiertas del scope VIGENTE (17º gate).
      if ((pendingByScope.get(activeScopeId) ?? 0) > 0) return false;
      if (!guard.isCurrent(token)) return false;
      setOwner(owner);
      return true;
    },
    beginMutation: () => {
      pendingByScope.set(activeScopeId, (pendingByScope.get(activeScopeId) ?? 0) + 1);
      mutationAttempt += 1;
      guard.invalidate();
      if (!disposed) setOwner(null);
      return { scopeId: activeScopeId, attempt: mutationAttempt };
    },
    isMutationCurrent: (ticket: MutationTicket) =>
      !disposed && ticket.scopeId === activeScopeId && ticket.attempt === mutationAttempt,
    isMutationScopeCurrent: (ticket: MutationTicket) =>
      !disposed && ticket.scopeId === activeScopeId,
    hasOpenMutations: (ticket: MutationTicket) =>
      (pendingByScope.get(ticket.scopeId) ?? 0) > 0,
    noteKeepError: (ticket: MutationTicket) => {
      // Solo scopes VIGENTES (19º gate): un keepError de un scope muerto
      // jamás tendrá consumidor (la purga de época ya corrió) — no se
      // reinsertan huérfanos. Las mutaciones stale solo por ATTEMPT del
      // mismo scope sí agregan (su grupo sigue vivo).
      if (disposed || ticket.scopeId !== activeScopeId) return;
      keepErrorByScope.set(ticket.scopeId, true);
    },
    consumeKeepError: (ticket: MutationTicket) => {
      const value = keepErrorByScope.get(ticket.scopeId) ?? false;
      keepErrorByScope.delete(ticket.scopeId);
      return value;
    },
    endMutation: (ticket: MutationTicket) => {
      // SIEMPRE decrementa (en el scope del boleto) — también para
      // mutaciones stale: ningún contador queda filtrado.
      const restantes = Math.max(0, (pendingByScope.get(ticket.scopeId) ?? 0) - 1);
      if (restantes === 0) pendingByScope.delete(ticket.scopeId);
      else pendingByScope.set(ticket.scopeId, restantes);
    },
    invalidate: () => {
      guard.invalidate();
      if (!disposed) setOwner(null);
    },
    dispose: () => {
      disposed = true;
      guard.invalidate();
    },
    isDisposed: () => disposed,
  };
}
